import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getHealthInsurancePayouts, deleteHealthInsurancePayout } from '../../model/api';
import Table from './common/Table';
import LoadingSpinner from './common/LoadingSpinner';

const HealthInsurancePaidHistory = () => {
    const [month, setMonth] = useState(''); // Empty means all months, or default to current
    const [loading, setLoading] = useState(false);
    const [payouts, setPayouts] = useState([]);

    const fetchHistory = async () => {
        setLoading(true);
        try {
            const params = { status: 'paid' };
            if (month) params.month = month;

            const result = await getHealthInsurancePayouts(params);
            if (result.ok) {
                setPayouts(result.data || []);
            } else {
                toast.error('Failed to load history');
            }
        } catch (error) {
            console.error('Fetch history error:', error);
            toast.error('Error loading history');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (payout) => {
        if (!window.confirm(`Are you sure you want to delete this payout record for ${payout.user_id?.name}?`)) {
            return;
        }

        try {
            const result = await deleteHealthInsurancePayout(payout._id);
            if (result.ok) {
                toast.success('Payout deleted successfully');
                fetchHistory(); // Refresh list
            } else {
                toast.error(result.error || 'Failed to delete payout');
            }
        } catch (error) {
            console.error('Delete error:', error);
            toast.error('Error deleting payout');
        }
    };

    useEffect(() => {
        fetchHistory();
    }, [month]);

    const columns = [
        { header: 'Partner', accessor: (row) => row.user_id?.name || 'Unknown' },
        { header: 'Month', accessor: 'month' },
        { header: 'Amount', accessor: (row) => `₹${row.payout_amount?.toLocaleString('en-IN')}` },
        { header: 'Transaction ID', accessor: (row) => <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">{row.transaction_id || '-'}</span> },
        { header: 'Method', accessor: (row) => row.payment_method || '-' },
        { header: 'Paid Date', accessor: (row) => row.paid_at ? new Date(row.paid_at).toLocaleDateString() : '-' },
        {
            header: 'Actions', accessor: (row) => (
                <button
                    onClick={() => handleDelete(row)}
                    className="text-red-600 hover:text-red-900 text-sm font-medium"
                >
                    Delete
                </button>
            )
        }
    ];

    return (
        <div className="space-y-6">
            <div className="bg-white p-4 rounded-lg shadow-sm flex items-center gap-4">
                <label className="font-medium text-gray-700">Filter by Month:</label>
                <input
                    type="month"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    className="border rounded px-3 py-2"
                />
                <button
                    onClick={() => setMonth('')}
                    className="text-sm text-indigo-600 hover:text-indigo-800 underline"
                >
                    Clear Filter
                </button>
            </div>

            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-800">Paid Payouts History</h3>
                </div>
                {loading ? <div className="p-8"><LoadingSpinner /></div> : (
                    <Table columns={columns} data={payouts} />
                )}
                {!loading && payouts.length === 0 && (
                    <div className="p-8 text-center text-gray-500">No paid records found.</div>
                )}
            </div>
        </div>
    );
};

export default HealthInsurancePaidHistory;
